import React, { useState, useRef, useEffect } from "react";
import { Input, Tooltip } from "antd";
import { EditOutlined, CheckOutlined, CloseOutlined, LoadingOutlined } from "@ant-design/icons";
import styled from "styled-components";

const { TextArea } = Input;

const Wrapper = styled.div`
  width: 100%;
`;

const LabelRow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
`;

const Label = styled.span`
  font-size: 14px;
  color: #64748b;
`;

const DescriptionText = styled.div`
  color: ${({ theme }) => theme?.settings?.textColor || "#64748b"};
  font-size: 14px;
  line-height: 1.5;
`;

const PlaceholderText = styled.div`
  color: ${({ theme }) => theme?.settings?.textColor || "#9ca3af"};
  font-size: 14px;
  font-style: italic;
`;

const EditIcon = styled(EditOutlined)`
  cursor: pointer;
  color: ${({ theme }) => theme?.settings?.textColor || "#83899F"};
  font-size: 14px;
  transition: color 0.2s ease;
  &:hover {
    color: ${({ theme }) => theme?.settings?.primary || "#3D2969"};
  }
`;

const ActionIcon = styled.span`
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  color: ${({ disabled, theme }) =>
    disabled
      ? theme?.settings?.textColor || "#ccc"
      : theme?.settings?.primary || "#3D2969"};
  font-size: 14px;
  transition: all 0.2s ease;
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
  display: inline-flex;
  align-items: center;
  &:hover {
    color: ${({ disabled, theme }) =>
      disabled
        ? theme?.settings?.textColor || "#ccc"
        : theme?.settings?.blueDarkColor || "#5F42DE"};
  }
`;

const CancelIcon = styled.span`
  cursor: pointer;
  color: ${({ theme }) => theme?.settings?.textColor || "#83899F"};
  font-size: 14px;
  transition: color 0.2s ease;
  display: inline-flex;
  align-items: center;
  &:hover {
    color: ${({ theme }) => theme?.settings?.orangeColor || "#E65B5B"};
  }
`;

const CharCount = styled.div`
  text-align: right;
  font-size: 12px;
  color: #999;
  margin-top: 4px;
`;

const StyledTextArea = styled(TextArea)`
  width: 100%;
  font-size: 14px;
  line-height: 1.5;
  border: 1px solid ${({ theme }) => theme?.settings?.borderColorLight || "#f0f0f0"};
  border-radius: 6px !important;
  resize: vertical;
  &:hover,
  &:focus {
    border-color: ${({ theme }) => theme?.settings?.primary || "#3D2969"} !important;
  }
`;

const ActionsWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const MAX_LENGTH = 500;

const EditableDescription = ({
  value,
  onSave,
  label = "Description",
  placeholder = "Add a description...",
  maxLength = MAX_LENGTH,
  rows = 3,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value || "");
  const [isSaving, setIsSaving] = useState(false);
  const inputRef = useRef(null);
  const wrapperRef = useRef(null);

  useEffect(() => {
    if (!isEditing) {
      setEditValue(value || "");
    }
  }, [value, isEditing]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditing]);

  // Click outside to cancel
  useEffect(() => {
    if (!isEditing) return;

    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        handleCancel();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isEditing]);

  const startEditing = () => {
    setEditValue(value || "");
    setIsEditing(true);
  };

  const handleCancel = () => {
    setEditValue(value || "");
    setIsEditing(false);
  };

  const handleSave = async () => {
    const trimmedValue = editValue.trim();

    if (trimmedValue === (value || "")) {
      setIsEditing(false);
      return;
    }

    setIsSaving(true);
    try {
      await onSave(trimmedValue || null);
      setIsEditing(false);
    } catch (error) {
      console.error("Failed to save description:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      e.preventDefault();
      handleCancel();
    }
  };

  const handleChange = (e) => {
    const newValue = e.target.value;
    if (newValue.length <= maxLength) {
      setEditValue(newValue);
    }
  };

  if (isEditing) {
    return (
      <Wrapper ref={wrapperRef}>
        <LabelRow>
          <Label>{label}</Label>
          <ActionsWrapper className="editable-description-actions">
            <Tooltip title="Save">
              <ActionIcon
                disabled={isSaving}
                onClick={() => !isSaving && handleSave()}
                tabIndex={0}
              >
                {isSaving ? <LoadingOutlined spin /> : <CheckOutlined />}
              </ActionIcon>
            </Tooltip>
            <Tooltip title="Cancel">
              <CancelIcon onClick={handleCancel} tabIndex={0}>
                <CloseOutlined />
              </CancelIcon>
            </Tooltip>
          </ActionsWrapper>
        </LabelRow>
        <StyledTextArea
          ref={inputRef}
          value={editValue}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          maxLength={maxLength}
          disabled={isSaving}
          rows={rows}
        />
        <CharCount>{editValue.length} / {maxLength}</CharCount>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <LabelRow>
        <Label>{label}</Label>
        <Tooltip title="Edit description">
          <EditIcon onClick={startEditing} />
        </Tooltip>
      </LabelRow>
      {value ? (
        <DescriptionText>{value}</DescriptionText>
      ) : (
        <PlaceholderText>{placeholder}</PlaceholderText>
      )}
    </Wrapper>
  );
};

export default EditableDescription;
